import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import PayrollJobsDock from './PayrollJobsDock';
import { 
    DollarSign, RefreshCw, Play, Check, Download, 
    FileText, Building2, Users, Calculator, AlertTriangle,
    FileCheck, Landmark, ClipboardList, Layers
} from 'lucide-react';
import logger from '@/lib/logger';

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

const STATUS_COLORS = {
    draft: 'bg-slate-500',
    calculated: 'bg-blue-500',
    hr_approved: 'bg-amber-500',
    finance_approved: 'bg-green-500',
    paid: 'bg-emerald-600',
    on_hold: 'bg-red-500'
};

// ===== Salary Register Sub-Component =====
const SalaryRegisterTab = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [regMonth, setRegMonth] = useState('');
    const [regDept, setRegDept] = useState('all');
    const formatCur = (a) => new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED' }).format(a || 0);

    // Build year-month options
    const monthOptions = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        monthOptions.push({
            value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
            label: d.toLocaleString('default', { month: 'long', year: 'numeric' })
        });
    }

    const fetchRegister = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (regMonth) params.append('month', regMonth);
            if (regDept && regDept !== 'all') params.append('department', regDept);
            const res = await api.get(`/hr/payroll/salary-register?${params.toString()}`);
            setData(res.data);
        } catch (e) { logger.error(e); toast.error('Failed to load salary register'); } finally { setLoading(false); }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchRegister(); }, []);

    return (
        <div className="space-y-4" data-testid="salary-register-view">
            <Card>
                <CardHeader className="flex-row items-center justify-between gap-4 pb-3">
                    <CardTitle className="text-base">Salary Register {data?.month ? `— ${data.month}` : ''}</CardTitle>
                    <div className="flex items-center gap-2">
                        <Select value={regMonth} onValueChange={setRegMonth}>
                            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Latest Month" /></SelectTrigger>
                            <SelectContent>
                                {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={regDept} onValueChange={setRegDept}>
                            <SelectTrigger className="w-[160px]"><SelectValue placeholder="All Depts" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="Sales">Sales</SelectItem>
                                <SelectItem value="Finance">Finance</SelectItem>
                                <SelectItem value="Customer Service">CS</SelectItem>
                                <SelectItem value="Mentors/Academics">Mentors</SelectItem>
                                <SelectItem value="Operations">Ops</SelectItem>
                                <SelectItem value="Marketing">Marketing</SelectItem>
                                <SelectItem value="HR">HR</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button size="sm" onClick={fetchRegister} disabled={loading}><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
                    </div>
                </CardHeader>
                {data && (
                    <>
                        <ScrollArea className="max-h-[500px]">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="sticky left-0 bg-background z-10">Employee</TableHead>
                                        <TableHead>Dept</TableHead>
                                        <TableHead className="text-right">Basic</TableHead>
                                        <TableHead className="text-right">Housing</TableHead>
                                        <TableHead className="text-right">Transport</TableHead>
                                        <TableHead className="text-right">Food</TableHead>
                                        <TableHead className="text-right">Phone</TableHead>
                                        <TableHead className="text-right">Other</TableHead>
                                        <TableHead className="text-right font-bold">Gross</TableHead>
                                        <TableHead className="text-right text-red-600">Deductions</TableHead>
                                        <TableHead className="text-right text-emerald-600">Additions</TableHead>
                                        <TableHead className="text-right font-bold">Net</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(data.register || []).map((r, i) => (
                                        <TableRow key={r.employee_id || r.employee_code || i}>
                                            <TableCell className="sticky left-0 bg-background z-10">
                                                <p className="font-medium text-sm">{r.employee_name}</p>
                                                <p className="text-xs text-muted-foreground">{r.employee_code}</p>
                                            </TableCell>
                                            <TableCell className="text-xs">{r.department}</TableCell>
                                            <TableCell className="text-right text-sm">{formatCur(r.basic_salary)}</TableCell>
                                            <TableCell className="text-right text-sm">{formatCur(r.housing_allowance)}</TableCell>
                                            <TableCell className="text-right text-sm">{formatCur(r.transport_allowance)}</TableCell>
                                            <TableCell className="text-right text-sm">{formatCur(r.food_allowance)}</TableCell>
                                            <TableCell className="text-right text-sm">{formatCur(r.phone_allowance)}</TableCell>
                                            <TableCell className="text-right text-sm">{formatCur(r.other_allowances)}</TableCell>
                                            <TableCell className="text-right font-bold text-sm">{formatCur(r.gross_salary)}</TableCell>
                                            <TableCell className="text-right text-red-600 text-sm">-{formatCur(r.total_deductions)}</TableCell>
                                            <TableCell className="text-right text-emerald-600 text-sm">+{formatCur(r.total_additions)}</TableCell>
                                            <TableCell className="text-right font-bold text-sm">{formatCur(r.net_salary)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {data.totals && (
                                        <TableRow className="bg-muted/50 font-bold">
                                            <TableCell className="sticky left-0 bg-muted/50 z-10">TOTALS ({data.employee_count})</TableCell>
                                            <TableCell></TableCell>
                                            <TableCell className="text-right">{formatCur(data.totals.basic)}</TableCell>
                                            <TableCell className="text-right">{formatCur(data.totals.housing)}</TableCell>
                                            <TableCell className="text-right">{formatCur(data.totals.transport)}</TableCell>
                                            <TableCell className="text-right">{formatCur(data.totals.food)}</TableCell>
                                            <TableCell className="text-right">{formatCur(data.totals.phone)}</TableCell>
                                            <TableCell className="text-right">{formatCur(data.totals.other)}</TableCell>
                                            <TableCell className="text-right">{formatCur(data.totals.gross)}</TableCell>
                                            <TableCell className="text-right text-red-600">-{formatCur(data.totals.deductions)}</TableCell>
                                            <TableCell className="text-right text-emerald-600">+{formatCur(data.totals.additions)}</TableCell>
                                            <TableCell className="text-right">{formatCur(data.totals.net)}</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    </>
                )}
            </Card>
        </div>
    );
};

// ===== Payslips Sub-Component =====
const PayslipsTab = () => {
    const [generating, setGenerating] = useState(false);
    const now = new Date();
    const [genMonth, setGenMonth] = useState(now.getMonth() + 1);
    const [genYear, setGenYear] = useState(now.getFullYear());

    const handleGenerate = async (employeeId = null) => {
        setGenerating(true);
        try {
            const params = new URLSearchParams({ month: genMonth, year: genYear });
            if (employeeId) params.append('employee_id', employeeId);
            const res = await api.post(`/hr/payroll/generate-payslips?${params.toString()}`);
            toast.success(res.data.message);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to generate payslips');
        } finally { setGenerating(false); }
    };

    return (
        <div className="space-y-4" data-testid="payslips-view">
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2"><FileCheck className="h-5 w-5" /> Generate Payslips</CardTitle>
                    <CardDescription>Generate payslips for all employees. Once generated, employees can view and download from their SSHR portal.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex items-end gap-4 flex-wrap">
                        <div className="space-y-1">
                            <Label className="text-xs">Month</Label>
                            <Select value={String(genMonth)} onValueChange={(v) => setGenMonth(Number(v))}>
                                <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {MONTHS.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Year</Label>
                            <Input type="number" value={genYear} onChange={(e) => setGenYear(Number(e.target.value))} className="w-[100px]" />
                        </div>
                        <Button onClick={() => handleGenerate()} disabled={generating} data-testid="generate-all-payslips-btn">
                            {generating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <FileCheck className="h-4 w-4 mr-2" />}
                            Generate All Payslips
                        </Button>
                    </div>
                    <div className="mt-4 p-4 rounded-lg bg-muted/40 border text-sm space-y-1">
                        <p className="font-medium">How it works:</p>
                        <ul className="list-disc list-inside text-muted-foreground space-y-0.5 text-xs">
                            <li>Payroll must be processed first for the selected month</li>
                            <li>Payslips are generated from payroll data and stored in the system</li>
                            <li>Employees can view/download payslips from their SSHR &gt; My Payslips</li>
                            <li>Re-generating replaces existing payslips for that month</li>
                        </ul>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

// ===== Anomalies Sub-Component =====
const AnomaliesTab = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const SEVERITY_COLORS = { high: 'bg-red-500', medium: 'bg-amber-500', low: 'bg-slate-400' };

    const fetchAnomalies = async () => {
        setLoading(true);
        try {
            const res = await api.get('/hr/payroll/anomalies');
            setData(res.data);
        } catch (e) { logger.error(e); } finally { setLoading(false); }
    };

    useEffect(() => { fetchAnomalies(); }, []);

    if (loading) return <div className="flex justify-center py-12"><RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

    return (
        <div className="space-y-4" data-testid="anomalies-view">
            {data && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold">{data.total_employees}</p><p className="text-xs text-muted-foreground">Total Employees</p></CardContent></Card>
                        <Card className="border-l-4 border-l-red-500"><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-red-600">{data.high_severity}</p><p className="text-xs text-muted-foreground">High Severity</p></CardContent></Card>
                        <Card className="border-l-4 border-l-amber-500"><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-amber-600">{data.medium_severity}</p><p className="text-xs text-muted-foreground">Medium</p></CardContent></Card>
                        <Card className="border-l-4 border-l-slate-400"><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-slate-500">{data.low_severity}</p><p className="text-xs text-muted-foreground">Low</p></CardContent></Card>
                    </div>
                    <Card>
                        <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Payroll Anomalies</CardTitle></CardHeader>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Employee</TableHead>
                                    <TableHead>Department</TableHead>
                                    <TableHead>Severity</TableHead>
                                    <TableHead>Issues</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(data.anomalies || []).map((a, i) => (
                                    <TableRow key={a.employee_id || i}>
                                        <TableCell>
                                            <p className="font-medium text-sm">{a.employee_name}</p>
                                            <p className="text-xs text-muted-foreground">{a.employee_id}</p>
                                        </TableCell>
                                        <TableCell>{a.department}</TableCell>
                                        <TableCell>
                                            <Badge className={`${SEVERITY_COLORS[a.max_severity]} text-white text-xs`}>
                                                {a.max_severity}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="space-y-1">
                                                {a.issues.map((issue, j) => (
                                                    <p key={j} className="text-xs">
                                                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${SEVERITY_COLORS[issue.severity]} mr-1.5`} />
                                                        {issue.message}
                                                    </p>
                                                ))}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(!data.anomalies || data.anomalies.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                                            No anomalies detected. All employee records look good!
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Card>
                </>
            )}
        </div>
    );
};

// ===== WPS Export Sub-Component =====

// Fix Attendance Dialog — lets HR set a specific day's status for an
// employee directly from the Pre-Payroll reconciliation table. Writes to
// the bulk-entry endpoint which also auto-creates an approved leave
// record when the status is annual_leave / sick_leave / on_leave. This
// closes the loop "see LOP → fix in one click" without leaving the
// payroll workspace.
//
// Supports BOTH single-row and bulk (multi-employee) mode:
//   - row: pass a single reconciliation row → single-employee fix
//   - rows: pass an array of rows → bulk-apply one status to N employees
const FixAttendanceDialog = ({ open, onClose, row, rows, month, year, onSaved }) => {
    const [date, setDate] = useState('');
    const [status, setStatus] = useState('present');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const bulkRows = Array.isArray(rows) && rows.length > 0 ? rows : null;
    const targetRows = bulkRows || (row ? [row] : []);
    const isBulk = !!bulkRows;
    const targetCount = targetRows.length;

    useEffect(() => {
        if (open && month && year) {
            const today = new Date();
            // Default to today if month/year matches, else day 1 of selected month
            const isCurrentMonth = today.getMonth() + 1 === month && today.getFullYear() === year;
            const d = isCurrentMonth ? today.getDate() : 1;
            setDate(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
            setStatus('present');
            setNotes('');
        }
    }, [open, month, year]);

    if (targetCount === 0) return null;

    const save = async () => {
        if (!date || !status) {
            toast.error('Pick a date and a status');
            return;
        }
        setSaving(true);
        try {
            const payload = targetRows.map(r => ({
                employee_id: r.employee_id,
                date,
                status,
                notes,
            }));
            await api.post('/hr/attendance/bulk-entry', payload);
            toast.success(
                isBulk
                    ? `Attendance updated for ${targetCount} employees on ${date}`
                    : `Attendance updated for ${targetRows[0].name} on ${date}`
            );
            onSaved?.();
            onClose();
        } catch (e) {
            logger.error(e);
            toast.error(e?.response?.data?.detail || 'Failed to update attendance');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid={isBulk ? 'fix-attendance-bulk-dialog' : 'fix-attendance-dialog'}>
                <DialogHeader>
                    <DialogTitle>
                        {isBulk
                            ? `Bulk Fix — ${targetCount} employees`
                            : `Fix attendance — ${targetRows[0].name}`}
                    </DialogTitle>
                    <CardDescription>
                        {isBulk
                            ? `Apply ONE status to all ${targetCount} selected employees on the chosen day. Leave statuses auto-create approved leaves so balances update immediately.`
                            : 'Set status for a specific day. Leave statuses auto-create an approved leave so the balance updates immediately.'}
                    </CardDescription>
                </DialogHeader>
                {isBulk && (
                    <div className="rounded border bg-muted/30 p-2 max-h-32 overflow-y-auto text-xs space-y-0.5">
                        {targetRows.slice(0, 30).map(r => (
                            <div key={r.employee_id} className="flex justify-between gap-2" data-testid={`bulk-target-${r.employee_id}`}>
                                <span className="font-medium truncate">{r.name}</span>
                                <span className="text-muted-foreground">{r.employee_code}</span>
                            </div>
                        ))}
                        {targetRows.length > 30 && (
                            <div className="text-center text-muted-foreground pt-1">+ {targetRows.length - 30} more…</div>
                        )}
                    </div>
                )}
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Date</Label>
                        <Input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            min={`${year}-${String(month).padStart(2, '0')}-01`}
                            data-testid="fix-attendance-date"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Status</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="fix-attendance-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="present">Present</SelectItem>
                                <SelectItem value="half_day">Half Day</SelectItem>
                                <SelectItem value="wfh">WFH</SelectItem>
                                <SelectItem value="late">Late (counts as present)</SelectItem>
                                <SelectItem value="annual_leave">Annual Leave</SelectItem>
                                <SelectItem value="sick_leave">Sick Leave</SelectItem>
                                <SelectItem value="on_leave">Other Leave</SelectItem>
                                <SelectItem value="absent">Absent (LOP)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">Notes (optional)</Label>
                        <Input
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="e.g. Approved by manager via WhatsApp"
                            data-testid="fix-attendance-notes"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving} data-testid="fix-attendance-cancel">Cancel</Button>
                    <Button onClick={save} disabled={saving} data-testid="fix-attendance-save">
                        {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                        {isBulk ? `Apply to ${targetCount}` : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ===== Reconciliation Sub-Component =====
const ReconciliationTab = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const n = new Date();
    const [recMonth, setRecMonth] = useState(n.getMonth() + 1);
    const [recYear, setRecYear] = useState(n.getFullYear());
    const [recDept, setRecDept] = useState('all');
    // Fix Attendance dialog state — clicking the Fix button opens a dialog
    // bound to the employee row so HR can correct attendance / mark a leave
    // for a specific day without leaving the payroll workspace.
    const [fixRow, setFixRow] = useState(null);
    // Bulk-select state: set of employee_ids ticked in the table + dialog visibility.
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkOpen, setBulkOpen] = useState(false);
    const formatCur = (a) => new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', maximumFractionDigits: 0 }).format(a || 0);

    const fetchRecon = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ month: recMonth, year: recYear });
            if (recDept && recDept !== 'all') params.append('department', recDept);
            const res = await api.get(`/hr/payroll/reconciliation?${params.toString()}`);
            setData(res.data);
            setSelectedIds(new Set()); // clear stale selections after a fresh load
        } catch (e) { logger.error(e); toast.error('Failed to load reconciliation data'); } finally { setLoading(false); }
    };

    const allRows = data?.reconciliation || [];
    const selectedRows = allRows.filter(r => selectedIds.has(r.employee_id));
    const allSelected = allRows.length > 0 && allRows.every(r => selectedIds.has(r.employee_id));
    const someSelected = selectedIds.size > 0 && !allSelected;

    const toggleRow = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        setSelectedIds(allSelected ? new Set() : new Set(allRows.map(r => r.employee_id)));
    };

    return (
        <div className="space-y-4" data-testid="reconciliation-view">
            <Card>
                <CardHeader className="flex-row items-center justify-between gap-4 pb-3">
                    <div>
                        <CardTitle className="text-base flex items-center gap-2"><ClipboardList className="h-5 w-5" /> Pre-Payroll Reconciliation</CardTitle>
                        <CardDescription className="text-xs">Verify attendance, leaves, and LOP before running payroll</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select value={String(recMonth)} onValueChange={(v) => setRecMonth(Number(v))}>
                            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {MONTHS.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Input type="number" value={recYear} onChange={(e) => setRecYear(Number(e.target.value))} className="w-[90px]" />
                        <Select value={recDept} onValueChange={setRecDept}>
                            <SelectTrigger className="w-[130px]"><SelectValue placeholder="All" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="Sales">Sales</SelectItem>
                                <SelectItem value="Finance">Finance</SelectItem>
                                <SelectItem value="Customer Service">CS</SelectItem>
                                <SelectItem value="Mentors/Academics">Mentors</SelectItem>
                                <SelectItem value="Operations">Ops</SelectItem>
                                <SelectItem value="Marketing">Marketing</SelectItem>
                                <SelectItem value="HR">HR</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button size="sm" onClick={fetchRecon} disabled={loading} data-testid="load-reconciliation-btn">
                            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
                            <span className="ml-1">Load</span>
                        </Button>
                        {selectedIds.size > 0 && (
                            <Button
                                size="sm"
                                variant="default"
                                className="bg-violet-600 hover:bg-violet-700"
                                onClick={() => setBulkOpen(true)}
                                data-testid="fix-bulk-btn"
                            >
                                <Layers className="h-4 w-4 mr-1" />
                                Fix Bulk ({selectedIds.size})
                            </Button>
                        )}
                    </div>
                </CardHeader>
                {data && (
                    <>
                        <div className="px-6 pb-3 grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div className="p-3 rounded bg-muted/40 text-center"><p className="text-lg font-bold">{data.working_days}</p><p className="text-[10px] text-muted-foreground">Working Days</p></div>
                            <div className="p-3 rounded bg-muted/40 text-center"><p className="text-lg font-bold">{data.total_employees}</p><p className="text-[10px] text-muted-foreground">Employees</p></div>
                            <div className="p-3 rounded bg-emerald-50 dark:bg-emerald-950/20 text-center"><p className="text-lg font-bold text-emerald-600">{data.employees_with_attendance}</p><p className="text-[10px] text-muted-foreground">With Attendance</p></div>
                            <div className="p-3 rounded bg-red-50 dark:bg-red-950/20 text-center"><p className="text-lg font-bold text-red-600">{data.employees_without_attendance}</p><p className="text-[10px] text-muted-foreground">No Attendance</p></div>
                            <div className="p-3 rounded bg-amber-50 dark:bg-amber-950/20 text-center"><p className="text-lg font-bold text-amber-600">{data.total_lop_days}</p><p className="text-[10px] text-muted-foreground">Total LOP Days</p></div>
                        </div>
                        <ScrollArea className="max-h-[500px]">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-10 sticky left-0 bg-background z-20">
                                            <Checkbox
                                                checked={allSelected ? true : (someSelected ? 'indeterminate' : false)}
                                                onCheckedChange={toggleAll}
                                                aria-label="Select all rows"
                                                data-testid="select-all-recon"
                                            />
                                        </TableHead>
                                        <TableHead className="sticky left-10 bg-background z-10">Employee</TableHead>
                                        <TableHead>Dept</TableHead>
                                        <TableHead className="text-right">Gross</TableHead>
                                        <TableHead className="text-center">Present</TableHead>
                                        <TableHead className="text-center">Late</TableHead>
                                        <TableHead className="text-center">Half</TableHead>
                                        <TableHead className="text-center">WFH</TableHead>
                                        <TableHead className="text-center text-violet-600">Approved Leave</TableHead>
                                        <TableHead className="text-center text-amber-600">Pending Leave</TableHead>
                                        <TableHead className="text-center text-red-600">LOP Days</TableHead>
                                        <TableHead className="text-right text-red-600">LOP Deduction</TableHead>
                                        <TableHead className="text-right text-emerald-600">Projected Net</TableHead>
                                        <TableHead className="text-center">Att %</TableHead>
                                        <TableHead className="text-center">Data</TableHead>
                                        <TableHead className="text-center">Fix</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(data.reconciliation || []).map(r => (
                                        <TableRow key={r.employee_id} className={!r.has_attendance ? 'bg-red-50/50 dark:bg-red-950/10' : r.lop_days > 0 ? 'bg-amber-50/30 dark:bg-amber-950/10' : ''}>
                                            <TableCell className="sticky left-0 bg-background z-20">
                                                <Checkbox
                                                    checked={selectedIds.has(r.employee_id)}
                                                    onCheckedChange={() => toggleRow(r.employee_id)}
                                                    aria-label={`Select ${r.name}`}
                                                    data-testid={`select-row-${r.employee_id}`}
                                                />
                                            </TableCell>
                                            <TableCell className="sticky left-10 bg-background z-10">
                                                <div>
                                                    <p className="font-medium text-sm">{r.name}</p>
                                                    <p className="text-[10px] text-muted-foreground">{r.employee_code}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-xs">{r.department}</TableCell>
                                            <TableCell className="text-right text-sm">{formatCur(r.gross_salary)}</TableCell>
                                            <TableCell className="text-center text-emerald-600 font-medium">{r.present_days}</TableCell>
                                            <TableCell className="text-center text-amber-600">{r.late_days || 0}</TableCell>
                                            <TableCell className="text-center text-orange-500">{r.half_days || 0}</TableCell>
                                            <TableCell className="text-center text-blue-500">{r.wfh_days || 0}</TableCell>
                                            <TableCell className="text-center text-violet-600 font-medium">{r.approved_leave_days || 0}</TableCell>
                                            <TableCell className="text-center text-amber-600">{r.pending_leave_days || 0}</TableCell>
                                            <TableCell className="text-center font-bold text-red-600">{r.lop_days || 0}</TableCell>
                                            <TableCell className="text-right text-red-600 text-sm">-{formatCur(r.lop_deduction)}</TableCell>
                                            <TableCell className="text-right font-bold text-sm text-emerald-600">{formatCur(r.projected_net)}</TableCell>
                                            <TableCell className="text-center">
                                                <Badge variant={r.attendance_pct >= 90 ? 'default' : r.attendance_pct >= 50 ? 'secondary' : 'destructive'} className="text-[10px]">
                                                    {r.attendance_pct}%
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {r.has_attendance ? (
                                                    <Badge className="bg-emerald-500 text-white text-[10px]">Yes</Badge>
                                                ) : (
                                                    <Badge className="bg-red-500 text-white text-[10px]">No</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-7 px-2 text-[10px]"
                                                    onClick={() => setFixRow(r)}
                                                    data-testid={`fix-attendance-btn-${r.employee_id}`}
                                                >
                                                    Fix
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    </>
                )}
                {!data && (
                    <div className="p-8 text-center text-muted-foreground">
                        Select month/year and click Load to view reconciliation data
                    </div>
                )}
            </Card>
            <FixAttendanceDialog
                open={!!fixRow}
                row={fixRow}
                month={recMonth}
                year={recYear}
                onClose={() => setFixRow(null)}
                onSaved={fetchRecon}
            />
            <FixAttendanceDialog
                open={bulkOpen}
                rows={selectedRows}
                month={recMonth}
                year={recYear}
                onClose={() => setBulkOpen(false)}
                onSaved={fetchRecon}
            />
        </div>
    );
};

const WPSExportTab = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const now = new Date();
    const [wpsMonth, setWpsMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
    const formatCur = (a) => new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED' }).format(a || 0);

    const fetchWPS = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/hr/payroll/wps-export?month=${wpsMonth}`);
            setData(res.data);
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load WPS data'); } finally { setLoading(false); }
    };

    const wpsMonthOptions = [];
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        wpsMonthOptions.push({
            value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
            label: d.toLocaleString('default', { month: 'long', year: 'numeric' })
        });
    }

    return (
        <div className="space-y-4" data-testid="wps-export-view">
            <Card>
                <CardHeader className="flex-row items-center justify-between gap-4 pb-3">
                    <div>
                        <CardTitle className="text-base flex items-center gap-2"><Landmark className="h-5 w-5" /> WPS Export</CardTitle>
                        <CardDescription className="text-xs">Generate UAE Wage Protection System file for bank transfer</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select value={wpsMonth} onValueChange={setWpsMonth}>
                            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {wpsMonthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button size="sm" onClick={fetchWPS} disabled={loading} data-testid="load-wps-btn">
                            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                            <span className="ml-2">Load</span>
                        </Button>
                    </div>
                </CardHeader>
                {data && (
                    <>
                        <div className="px-6 pb-3">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="p-3 rounded bg-muted/40 text-center"><p className="text-lg font-bold">{data.total_records}</p><p className="text-xs text-muted-foreground">Employees</p></div>
                                <div className="p-3 rounded bg-muted/40 text-center"><p className="text-lg font-bold text-emerald-600">{formatCur(data.total_net)}</p><p className="text-xs text-muted-foreground">Total Net</p></div>
                                <div className="p-3 rounded bg-muted/40 text-center"><p className="text-lg font-bold">{data.total_records - data.missing_bank}</p><p className="text-xs text-muted-foreground">Ready</p></div>
                                <div className="p-3 rounded bg-muted/40 text-center"><p className="text-lg font-bold text-red-600">{data.missing_bank}</p><p className="text-xs text-muted-foreground">Missing Bank</p></div>
                            </div>
                        </div>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Employee</TableHead>
                                    <TableHead>Emirates ID</TableHead>
                                    <TableHead>Bank</TableHead>
                                    <TableHead>IBAN</TableHead>
                                    <TableHead className="text-right">Basic</TableHead>
                                    <TableHead className="text-right">Housing</TableHead>
                                    <TableHead className="text-right">Transport</TableHead>
                                    <TableHead className="text-right">Other</TableHead>
                                    <TableHead className="text-right font-bold">Net Salary</TableHead>
                                    <TableHead className="text-center">Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(data.records || []).map((r, i) => (
                                    <TableRow key={r.employee_id || r.employee_code || i} className={!r.has_bank_details ? 'bg-red-50 dark:bg-red-950/20' : ''}>
                                        <TableCell>
                                            <p className="font-medium text-sm">{r.employee_name}</p>
                                            <p className="text-xs text-muted-foreground">{r.employee_code}</p>
                                        </TableCell>
                                        <TableCell className="text-xs font-mono">{r.emirates_id || '-'}</TableCell>
                                        <TableCell className="text-xs">{r.bank_name || '-'}</TableCell>
                                        <TableCell className="text-xs font-mono">{r.iban || '-'}</TableCell>
                                        <TableCell className="text-right text-sm">{formatCur(r.basic_salary)}</TableCell>
                                        <TableCell className="text-right text-sm">{formatCur(r.housing_allowance)}</TableCell>
                                        <TableCell className="text-right text-sm">{formatCur(r.transport_allowance)}</TableCell>
                                        <TableCell className="text-right text-sm">{formatCur(r.other_allowances)}</TableCell>
                                        <TableCell className="text-right font-bold text-sm">{formatCur(r.net_salary)}</TableCell>
                                        <TableCell className="text-center">
                                            {r.has_bank_details ? (
                                                <Badge className="bg-emerald-500 text-white text-xs">Ready</Badge>
                                            ) : (
                                                <Badge className="bg-red-500 text-white text-xs">Missing</Badge>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
            </Card>
        </div>
    );
};

// ===== Bonuses Tab — HR-only manual bonus / incentive entries =====
const BonusesTab = ({ year, month }) => {
    const monthStr = `${year}-${String(month).padStart(2, '0')}`;
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [newEmpId, setNewEmpId] = useState('');
    const [newAmount, setNewAmount] = useState('');
    const [newReason, setNewReason] = useState('');

    const fetchItems = async () => {
        try {
            setLoading(true);
            const [bonusRes, empRes] = await Promise.all([
                api.get(`/hr/bonus-adjustments?month=${monthStr}`),
                api.get('/hr/employees?status=active'),
            ]);
            setItems(bonusRes.data?.items || []);
            setEmployees(empRes.data || []);
        } catch (e) {
            logger.error('Bonuses fetch:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchItems(); }, [monthStr]);

    const handleAdd = async () => {
        if (!newEmpId || !newAmount) {
            toast.error('Select an employee and enter amount');
            return;
        }
        try {
            setAdding(true);
            await api.post('/hr/bonus-adjustments', {
                employee_id: newEmpId,
                month: monthStr,
                amount: parseFloat(newAmount),
                reason: newReason || '',
            });
            toast.success('Bonus added');
            setNewEmpId(''); setNewAmount(''); setNewReason('');
            fetchItems();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to add bonus');
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Remove this bonus?')) return;
        try {
            await api.delete(`/hr/bonus-adjustments/${id}`);
            toast.success('Bonus removed');
            fetchItems();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to delete');
        }
    };

    const total = items.reduce((s, i) => s + (i.amount || 0), 0);

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle>Manual Bonus / Incentive — {monthStr}</CardTitle>
                    <CardDescription>
                        HR-only. These are picked up automatically the next time payroll is run for this month,
                        shown as a separate <strong>Bonus / Incentive</strong> line on each payslip.
                        Edits are locked once the batch is HR-approved.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <div className="space-y-1">
                            <Label>Employee</Label>
                            <Select value={newEmpId} onValueChange={setNewEmpId}>
                                <SelectTrigger data-testid="bonus-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                                <SelectContent>
                                    {employees.map(e => (
                                        <SelectItem key={e.id} value={e.id}>
                                            {e.full_name} — {e.employee_id}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label>Amount (AED)</Label>
                            <Input type="number" value={newAmount} onChange={e => setNewAmount(e.target.value)}
                                placeholder="e.g. 500" data-testid="bonus-amount-input" />
                        </div>
                        <div className="space-y-1">
                            <Label>Reason (optional)</Label>
                            <Input value={newReason} onChange={e => setNewReason(e.target.value)}
                                placeholder="e.g. Excellence Award" data-testid="bonus-reason-input" />
                        </div>
                        <Button onClick={handleAdd} disabled={adding} data-testid="bonus-add-btn">
                            {adding ? 'Adding…' : 'Add Bonus'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle>Active Bonuses — {monthStr}</CardTitle>
                        <CardDescription>{items.length} entries · AED {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchItems}>
                        <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                    </Button>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="text-center py-8 text-muted-foreground">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            No manual bonuses recorded for {monthStr}.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Employee</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Reason</TableHead>
                                    <TableHead>Added By</TableHead>
                                    <TableHead>When</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map(it => (
                                    <TableRow key={it.id}>
                                        <TableCell className="font-medium">{it.employee_name}</TableCell>
                                        <TableCell>AED {Number(it.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{it.reason || '—'}</TableCell>
                                        <TableCell className="text-sm">{it.added_by_name || '—'}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {it.created_at ? new Date(it.created_at).toLocaleDateString() : '—'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm"
                                                onClick={() => handleDelete(it.id)}
                                                data-testid={`bonus-delete-${it.id}`}>
                                                Remove
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};



const PayrollPage = () => {
    const [batches, setBatches] = useState([]);
    const [payroll, setPayroll] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('batches');
    const [showRunModal, setShowRunModal] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(null);
    
    const currentDate = new Date();
    const [runMonth, setRunMonth] = useState(currentDate.getMonth() + 1);
    const [runYear, setRunYear] = useState(currentDate.getFullYear());
    const [runDepartment, setRunDepartment] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const batchesRes = await api.get('/hr/payroll/batches');
            setBatches(batchesRes.data || []);
        } catch (error) {
            logger.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPayrollForMonth = async (month) => {
        try {
            const res = await api.get(`/hr/payroll?month=${month}`);
            setPayroll(res.data || []);
        } catch (error) {
            logger.error('Error:', error);
        }
    };

    const handleRunPayroll = async () => {
        try {
            const data = {
                month: runMonth,
                year: runYear,
                department: runDepartment || null
            };
            const res = await api.post('/hr/payroll/run', data);
            const jobId = res.data?.job_id;
            if (!jobId) {
                toast.success(res.data?.message || 'Payroll started');
                setShowRunModal(false);
                fetchData();
                return;
            }
            toast.info('Payroll started in background — tracking progress…');
            setShowRunModal(false);
            // Poll until completion (max 5 min)
            const pollStart = Date.now();
            const poll = async () => {
                if (Date.now() - pollStart > 5 * 60_000) {
                    toast.warning('Payroll job still running — check back later');
                    fetchData();
                    return;
                }
                try {
                    const j = await api.get(`/hr/payroll/jobs/${jobId}`);
                    const job = j.data || {};
                    if (job.status === 'completed') {
                        const sum = job.result_summary || {};
                        toast.success(`Payroll complete — ${sum.employee_count || 0} employees · Net ${(sum.total_net || 0).toLocaleString()}`);
                        fetchData();
                        return;
                    }
                    if (job.status === 'failed') {
                        toast.error(`Payroll failed: ${job.error || 'Unknown error'}`);
                        fetchData();
                        return;
                    }
                    setTimeout(poll, 3000);
                } catch (err) {
                    toast.error('Could not check job status');
                }
            };
            setTimeout(poll, 2000);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to run payroll');
        }
    };

    const handleApprove = async (batchId, level) => {
        try {
            await api.put(`/hr/payroll/batch/${batchId}/approve?approval_level=${level}`);
            toast.success(`Payroll ${level} approved`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Approval failed');
        }
    };

    const handleMarkPaid = async (batchId) => {
        try {
            await api.put(`/hr/payroll/batch/${batchId}/pay`);
            toast.success('Payroll marked as paid');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Action failed');
        }
    };

    const viewBatchDetails = (batch) => {
        setSelectedBatch(batch);
        fetchPayrollForMonth(batch.month);
        setActiveTab('details');
    };

    const formatCurrency = (amount, currency = 'AED') => {
        const code = (currency || 'AED').toUpperCase();
        const locales = { AED: 'en-AE', INR: 'en-IN', USD: 'en-US' };
        try {
            return new Intl.NumberFormat(locales[code] || 'en-AE', {
                style: 'currency',
                currency: code,
            }).format(amount || 0);
        } catch {
            return `${code} ${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
        }
    };

    // Split the additions array on each payroll record into its three categories
    // (Commission / Bonus / Manual Bonus) — powers the dedicated table columns.
    const splitAdditions = (rec) => {
        const adds = rec?.additions || [];
        let commission = 0, bonus = 0, manualBonus = 0;
        const commNotes = [], bonusNotes = [];
        for (const a of adds) {
            const amt = a.amount || 0;
            if (a.type === 'commission') {
                commission += amt;
                commNotes.push(a.description);
            } else if (a.type === 'bonus') {
                bonus += amt;
                bonusNotes.push(a.description);
            } else if (a.type === 'manual_bonus') {
                manualBonus += amt;
                bonusNotes.push(a.description);
            }
        }
        return {
            commission,
            bonus: bonus + manualBonus,
            commNote: commNotes.join('; '),
            bonusNote: bonusNotes.join('; '),
        };
    };

    // Totals for the Details table grouped by currency so AED + INR don't mix.
    const computeDetailTotals = (records) => {
        const byCur = {};
        for (const r of records || []) {
            const cur = (r.currency || 'AED').toUpperCase();
            if (!byCur[cur]) {
                byCur[cur] = { basic: 0, allowances: 0, gross: 0, deductions: 0,
                               commission: 0, bonus: 0, net: 0, count: 0 };
            }
            const bucket = byCur[cur];
            bucket.count += 1;
            bucket.basic += r.basic_salary || 0;
            bucket.allowances += (r.housing_allowance || 0) + (r.transport_allowance || 0)
                               + (r.food_allowance || 0) + (r.phone_allowance || 0);
            bucket.gross += r.gross_salary || 0;
            bucket.deductions += r.total_deductions || 0;
            const { commission, bonus } = splitAdditions(r);
            bucket.commission += commission;
            bucket.bonus += bonus;
            bucket.net += r.net_salary || 0;
        }
        return byCur;
    };

    const totalGross = batches.reduce((sum, b) => sum + (b.total_gross || 0), 0);
    const totalNet = batches.reduce((sum, b) => sum + (b.total_net || 0), 0);
    // Aggregate per-currency totals across all batches
    const totalsByCurrency = batches.reduce((acc, b) => {
        const list = b.totals_by_currency || [];
        if (list.length === 0 && (b.total_gross || b.total_net)) {
            list.push({ currency: 'AED', total_gross: b.total_gross || 0, total_net: b.total_net || 0 });
        }
        for (const t of list) {
            if (!acc[t.currency]) acc[t.currency] = { gross: 0, net: 0 };
            acc[t.currency].gross += t.total_gross || 0;
            acc[t.currency].net += t.total_net || 0;
        }
        return acc;
    }, {});

    return (
        <div className="space-y-6" data-testid="payroll-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Payroll</h1>
                    <p className="text-muted-foreground">Process and manage employee payroll</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={fetchData}>
                        <RefreshCw className="h-4 w-4 mr-2" />Refresh
                    </Button>
                    <Button onClick={() => setShowRunModal(true)} data-testid="run-payroll-btn">
                        <Play className="h-4 w-4 mr-2" />Run Payroll
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/30">
                                <FileText className="h-6 w-6" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{batches.length}</p>
                                <p className="text-sm text-muted-foreground">Payroll Batches</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-lg bg-green-100 text-green-600 dark:bg-green-900/30">
                                <DollarSign className="h-6 w-6" />
                            </div>
                            <div className="flex-1 min-w-0">
                                {Object.keys(totalsByCurrency).length > 1 ? (
                                    <div className="space-y-0.5">
                                        {Object.entries(totalsByCurrency).map(([cur, t]) => (
                                            <p key={cur} className="text-lg font-bold">
                                                {formatCurrency(t.gross, cur)}
                                            </p>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-2xl font-bold">
                                        {Object.entries(totalsByCurrency)[0]
                                            ? formatCurrency(Object.entries(totalsByCurrency)[0][1].gross, Object.entries(totalsByCurrency)[0][0])
                                            : formatCurrency(totalGross)}
                                    </p>
                                )}
                                <p className="text-sm text-muted-foreground">Total Gross</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-lg bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30">
                                <Calculator className="h-6 w-6" />
                            </div>
                            <div className="flex-1 min-w-0">
                                {Object.keys(totalsByCurrency).length > 1 ? (
                                    <div className="space-y-0.5">
                                        {Object.entries(totalsByCurrency).map(([cur, t]) => (
                                            <p key={cur} className="text-lg font-bold">
                                                {formatCurrency(t.net, cur)}
                                            </p>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-2xl font-bold">
                                        {Object.entries(totalsByCurrency)[0]
                                            ? formatCurrency(Object.entries(totalsByCurrency)[0][1].net, Object.entries(totalsByCurrency)[0][0])
                                            : formatCurrency(totalNet)}
                                    </p>
                                )}
                                <p className="text-sm text-muted-foreground">Total Net</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-900/30">
                                <Users className="h-6 w-6" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">
                                    {batches.reduce((sum, b) => sum + (b.employee_count || 0), 0)}
                                </p>
                                <p className="text-sm text-muted-foreground">Employees Processed</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="flex-wrap">
                    <TabsTrigger value="reconciliation" data-testid="reconciliation-tab">Reconciliation</TabsTrigger>
                    <TabsTrigger value="batches">Payroll Batches</TabsTrigger>
                    <TabsTrigger value="details" disabled={!selectedBatch}>
                        {selectedBatch ? `Details: ${selectedBatch.month}` : 'Details'}
                    </TabsTrigger>
                    <TabsTrigger value="salary-register" data-testid="salary-register-tab">Salary Register</TabsTrigger>
                    <TabsTrigger value="payslips" data-testid="payslips-tab">Payslips</TabsTrigger>
                    <TabsTrigger value="bonuses" data-testid="bonuses-tab">Bonuses</TabsTrigger>
                    <TabsTrigger value="anomalies" data-testid="anomalies-tab">Anomalies</TabsTrigger>
                    <TabsTrigger value="wps" data-testid="wps-tab">WPS Export</TabsTrigger>
                </TabsList>

                {/* Reconciliation Tab */}
                <TabsContent value="reconciliation" className="mt-4">
                    <ReconciliationTab />
                </TabsContent>

                <TabsContent value="batches" className="mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Payroll Batches</CardTitle>
                        </CardHeader>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Month</TableHead>
                                    <TableHead>Department</TableHead>
                                    <TableHead>Employees</TableHead>
                                    <TableHead>Total Gross</TableHead>
                                    <TableHead>Total Net</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Created By</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {batches.map((batch) => (
                                    <TableRow key={batch.id}>
                                        <TableCell className="font-medium">{batch.month}</TableCell>
                                        <TableCell>{batch.department || 'All'}</TableCell>
                                        <TableCell>{batch.employee_count}</TableCell>
                                        <TableCell>
                                            {batch.totals_by_currency?.length ? (
                                                <div className="space-y-0.5 text-xs">
                                                    {batch.totals_by_currency.map(t => (
                                                        <div key={t.currency}>
                                                            <span className="text-muted-foreground">{t.currency}:</span>{' '}
                                                            {formatCurrency(t.total_gross, t.currency)}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : formatCurrency(batch.total_gross)}
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            {batch.totals_by_currency?.length ? (
                                                <div className="space-y-0.5 text-xs">
                                                    {batch.totals_by_currency.map(t => (
                                                        <div key={t.currency}>
                                                            <span className="text-muted-foreground">{t.currency}:</span>{' '}
                                                            {formatCurrency(t.total_net, t.currency)}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : formatCurrency(batch.total_net)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={`${STATUS_COLORS[batch.status]} text-white`}>
                                                {batch.status?.replace(/_/g, ' ')}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{batch.created_by_name}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button 
                                                    size="sm" 
                                                    variant="outline"
                                                    onClick={() => viewBatchDetails(batch)}
                                                >
                                                    View
                                                </Button>
                                                {batch.status === 'draft' && (
                                                    <Button 
                                                        size="sm"
                                                        onClick={() => handleApprove(batch.id, 'hr')}
                                                    >
                                                        HR Approve
                                                    </Button>
                                                )}
                                                {batch.status === 'hr_approved' && (
                                                    <Button 
                                                        size="sm"
                                                        onClick={() => handleApprove(batch.id, 'finance')}
                                                    >
                                                        Finance Approve
                                                    </Button>
                                                )}
                                                {batch.status === 'finance_approved' && (
                                                    <Button 
                                                        size="sm"
                                                        className="bg-emerald-600 hover:bg-emerald-700"
                                                        onClick={() => handleMarkPaid(batch.id)}
                                                    >
                                                        Mark Paid
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {batches.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                            No payroll batches. Click "Run Payroll" to process.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>

                <TabsContent value="details" className="mt-4">
                    {selectedBatch && (
                        <Card>
                            <CardHeader>
                                <CardTitle>Payroll Details - {selectedBatch.month}</CardTitle>
                                <CardDescription>
                                    Showing {payroll.length} employees across {Object.keys(computeDetailTotals(payroll)).length} currencies.
                                    Totals row shows per-currency sums at the bottom.
                                </CardDescription>
                            </CardHeader>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Employee</TableHead>
                                            <TableHead>Department</TableHead>
                                            <TableHead>Currency</TableHead>
                                            <TableHead className="text-right">Basic</TableHead>
                                            <TableHead className="text-right">Allowances</TableHead>
                                            <TableHead className="text-right">Gross</TableHead>
                                            <TableHead className="text-right">Deductions</TableHead>
                                            <TableHead className="text-right">Commission</TableHead>
                                            <TableHead className="text-right">Bonus</TableHead>
                                            <TableHead className="text-right">Net Salary</TableHead>
                                            <TableHead>Comments</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {payroll.map((record) => {
                                            const cur = record.currency || 'AED';
                                            const allowances = (record.housing_allowance || 0) +
                                                (record.transport_allowance || 0) +
                                                (record.food_allowance || 0) +
                                                (record.phone_allowance || 0);
                                            const { commission, bonus, commNote, bonusNote } = splitAdditions(record);
                                            const dedNotes = (record.deductions || []).map(d => d.description).join('; ');
                                            const comments = [commNote, bonusNote, dedNotes].filter(Boolean).join(' | ');
                                            return (
                                                <TableRow key={record.id} data-testid={`payroll-row-${record.id}`}>
                                                    <TableCell>
                                                        <div>
                                                            <p className="font-medium">{record.employee_name}</p>
                                                            <p className="text-xs text-muted-foreground">{record.employee_code}</p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>{record.department}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className="text-[10px]">{cur}</Badge>
                                                    </TableCell>
                                                    <TableCell className="text-right">{formatCurrency(record.basic_salary, cur)}</TableCell>
                                                    <TableCell className="text-right">{formatCurrency(allowances, cur)}</TableCell>
                                                    <TableCell className="text-right">{formatCurrency(record.gross_salary, cur)}</TableCell>
                                                    <TableCell className="text-right text-red-600">
                                                        {record.total_deductions ? '-' : ''}{formatCurrency(record.total_deductions, cur)}
                                                    </TableCell>
                                                    <TableCell className="text-right text-blue-600">
                                                        {commission ? '+' : ''}{formatCurrency(commission, cur)}
                                                    </TableCell>
                                                    <TableCell className="text-right text-emerald-600">
                                                        {bonus ? '+' : ''}{formatCurrency(bonus, cur)}
                                                    </TableCell>
                                                    <TableCell className="text-right font-bold">
                                                        {formatCurrency(record.net_salary, cur)}
                                                    </TableCell>
                                                    <TableCell className="max-w-[260px]">
                                                        <p className="text-xs text-muted-foreground truncate" title={comments}>
                                                            {comments || '—'}
                                                        </p>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                                {/* Per-currency totals */}
                                {payroll.length > 0 && (() => {
                                    const totals = computeDetailTotals(payroll);
                                    return (
                                        <div className="border-t bg-muted/30">
                                            <Table>
                                                <TableBody>
                                                    {Object.entries(totals).map(([cur, t]) => (
                                                        <TableRow key={cur} className="font-semibold bg-muted/40"
                                                            data-testid={`payroll-totals-${cur}`}>
                                                            <TableCell className="font-bold">
                                                                Totals — {cur}
                                                            </TableCell>
                                                            <TableCell className="text-xs text-muted-foreground">
                                                                {t.count} employee{t.count === 1 ? '' : 's'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Badge className="text-[10px]">{cur}</Badge>
                                                            </TableCell>
                                                            <TableCell className="text-right">{formatCurrency(t.basic, cur)}</TableCell>
                                                            <TableCell className="text-right">{formatCurrency(t.allowances, cur)}</TableCell>
                                                            <TableCell className="text-right">{formatCurrency(t.gross, cur)}</TableCell>
                                                            <TableCell className="text-right text-red-600">
                                                                {t.deductions ? '-' : ''}{formatCurrency(t.deductions, cur)}
                                                            </TableCell>
                                                            <TableCell className="text-right text-blue-600">
                                                                {t.commission ? '+' : ''}{formatCurrency(t.commission, cur)}
                                                            </TableCell>
                                                            <TableCell className="text-right text-emerald-600">
                                                                {t.bonus ? '+' : ''}{formatCurrency(t.bonus, cur)}
                                                            </TableCell>
                                                            <TableCell className="text-right font-bold">
                                                                {formatCurrency(t.net, cur)}
                                                            </TableCell>
                                                            <TableCell />
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    );
                                })()}
                            </div>
                        </Card>
                    )}
                </TabsContent>

                {/* Salary Register Tab */}
                <TabsContent value="salary-register" className="mt-4">
                    <SalaryRegisterTab />
                </TabsContent>

                {/* Payslips Tab */}
                <TabsContent value="payslips" className="mt-4">
                    <PayslipsTab />
                </TabsContent>

                {/* Bonuses Tab — HR-only manual bonus / incentive entries */}
                <TabsContent value="bonuses" className="mt-4">
                    <BonusesTab year={runYear} month={runMonth} />
                </TabsContent>

                {/* Anomalies Tab */}
                <TabsContent value="anomalies" className="mt-4">
                    <AnomaliesTab />
                </TabsContent>

                {/* WPS Export Tab */}
                <TabsContent value="wps" className="mt-4">
                    <WPSExportTab />
                </TabsContent>
            </Tabs>

            {/* Run Payroll Modal */}
            <Dialog open={showRunModal} onOpenChange={setShowRunModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Run Payroll</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Month</Label>
                                <Select value={String(runMonth)} onValueChange={(v) => setRunMonth(Number(v))}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MONTHS.map(m => (
                                            <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Year</Label>
                                <Input 
                                    type="number" 
                                    value={runYear}
                                    onChange={(e) => setRunYear(Number(e.target.value))}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Department (Optional)</Label>
                            <Select value={runDepartment} onValueChange={setRunDepartment}>
                                <SelectTrigger>
                                    <SelectValue placeholder="All Departments" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Departments</SelectItem>
                                    <SelectItem value="Sales">Sales</SelectItem>
                                    <SelectItem value="Finance">Finance</SelectItem>
                                    <SelectItem value="Customer Service">Customer Service</SelectItem>
                                    <SelectItem value="Mentors/Academics">Mentors/Academics</SelectItem>
                                    <SelectItem value="Operations">Operations</SelectItem>
                                    <SelectItem value="Marketing">Marketing</SelectItem>
                                    <SelectItem value="HR">HR</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            This will calculate payroll for all active employees based on their salary structure and attendance records.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRunModal(false)}>Cancel</Button>
                        <Button onClick={handleRunPayroll}>Run Payroll</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <PayrollJobsDock />
        </div>
    );
};

export default PayrollPage;
